import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import {
  type InlineRootElement,
  InlineSpecExtension,
  ZERO_WIDTH_FOR_EMBED_NODE,
} from '@blocksuite/std/inline';
import type { ExtensionType } from '@blocksuite/store';
import { html, nothing } from 'lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { z } from 'zod';

export type AffineInlineRootElement = InlineRootElement<AffineTextAttributes>;

export const BoldInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'bold',
    schema: z.object({
      bold: z.literal(true).optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.bold;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const ItalicInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'italic',
    schema: z.object({
      italic: z.literal(true).optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.italic;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const UnderlineInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'underline',
    schema: z.object({
      underline: z.literal(true).optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.underline;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const StrikeInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'strike',
    schema: z.object({
      strike: z.literal(true).optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.strike;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const CodeInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'inline-code',
    schema: z.object({
      code: z.literal(true).optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.code;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const BackgroundInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'background',
    schema: z.object({
      background: z.string().optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.background;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const ColorInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'color',
    schema: z.object({
      color: z.string().optional().nullable().catch(undefined),
    }),
    match: delta => {
      return !!delta.attributes?.color;
    },
    renderer: ({ delta }) => {
      return html`<affine-text .delta=${delta}></affine-text>`;
    },
  });

export const ImageInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'image',
    schema: z.object({
      image: z
        .object({
          src: z.string(),
          sourceId: z.string().optional().nullable().catch(undefined),
          alt: z.string().optional().nullable().catch(''),
          title: z.string().optional().nullable().catch(''),
          loading: z.boolean().optional().nullable().catch(undefined),
          error: z.string().optional().nullable().catch(undefined),
        })
        .optional()
        .nullable()
        .catch(undefined),
    }),
    match: delta => !!delta.attributes?.image?.src,
    renderer: ({ delta }) => {
      const imageAttrs = delta.attributes?.image as any;
      let src = imageAttrs?.src ?? '';
      let alt = imageAttrs?.alt ?? '';
      const title = imageAttrs?.title ?? '';
      const loading = imageAttrs?.loading;
      const error = imageAttrs?.error;

      if (src.startsWith('assets/')) {
        const meta = JSON.parse(alt);
        if (meta && typeof meta === 'object') {
          src = meta.originalUrl;
        }
      }

      // 基于 src 生成稳定的 key，让编辑器在 split 时能识别这是同一个节点
      const imageKey = hashString(src);
      const imgId = `inline-image-${imageKey}`;

      return html`<span
        data-inline-image-key=${imageKey}
        data-inline-image-src=${src}
        data-inline-no-toolbar
        style="
          display:inline-block;
          width:100%;
          max-width:100%;
        "
      >
        <img
          id=${imgId}
          data-inline-image-key=${imageKey}
          data-inline-image-src=${src}
          src=${src}
          alt=${alt}
          title=${title}
          style="width:100%;height:auto;border:1px solid rgba(0,0,0,0.05);overflow:hidden;border-radius:8px;"
          loading="lazy"
          decoding="async"
          fetchpriority="low"
        />
        <v-text .str=${ZERO_WIDTH_FOR_EMBED_NODE}></v-text>
      </span>`;
    },
    embed: true,
  });

export const VideoInlineSpecExtension =
  InlineSpecExtension<AffineTextAttributes>({
    name: 'video',
    schema: z.object({
      video: z
        .object({
          src: z.string(),
          alt: z.string().optional().nullable().catch(undefined),
          title: z.string().optional().nullable().catch(undefined),
        })
        .optional()
        .nullable()
        .catch(undefined),
    }),
    match: delta => !!delta.attributes?.video?.src,
    renderer: ({ delta }) => {
      const src = delta.attributes?.video?.src ?? '';
      const alt = delta.attributes?.video?.alt ?? '';
      const title = delta.attributes?.video?.title ?? '';
      // 基于 src 生成稳定的 key，让编辑器在 split 时能识别这是同一个节点
      const videoKey = src
        .split('')
        .reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0)
        .toString(36);
      const videoId = `inline-video-${videoKey}`;

      return html`<span
        data-inline-video-key=${videoKey}
        data-inline-video-src=${src}
        data-inline-no-toolbar
        style="
          display:inline-block;
          width:100%;
          max-width:100%;
          overflow:hidden;
        "
      >
        <video
          id=${videoId}
          data-inline-video-key=${videoKey}
          data-inline-video-src=${src}
          src=${src}
          controls
          title=${title || alt || ''}
          style="width:100%;height:auto;border:1px solid rgba(0,0,0,0.05);overflow:hidden;border-radius:8px;"
          preload="metadata"
        >
          ${alt ? html`<track kind="captions" label=${alt} />` : ''}
        </video>
        <v-text .str=${ZERO_WIDTH_FOR_EMBED_NODE}></v-text>
      </span>`;
    },
    embed: true,
  });

export const InlineSpecExtensions: ExtensionType[] = [
  BoldInlineSpecExtension,
  ItalicInlineSpecExtension,
  UnderlineInlineSpecExtension,
  StrikeInlineSpecExtension,
  CodeInlineSpecExtension,
  BackgroundInlineSpecExtension,
  ColorInlineSpecExtension,
  ImageInlineSpecExtension,
  VideoInlineSpecExtension,
];

function hashString(value: string): string {
  let hash = 0;
  for (const ch of value) {
    hash = ((hash << 5) - hash + ch.charCodeAt(0)) | 0;
  }
  return hash.toString(36);
}
